/**
 * Pure economy functions. Nothing here touches the DOM or the clock, so the
 * same code serves the live tick, offline progress, and the unit tests.
 */

import type { GameState } from './state';
import { DINOS } from '../data/dinos';
import type { DinoDef } from '../data/dinos';
import { UPGRADES_BY_ID } from '../data/upgrades';
import type { UpgradeDef } from '../data/upgrades';
import { ACHIEVEMENTS } from '../data/achievements';
import type { AchievementDef } from '../data/achievements';
import { REPEAT_GROWTH, SHOP_ITEMS_BY_ID } from '../data/shop';
import type { ShopItemDef } from '../data/shop';
import { addItem, evolutionMultiplier } from './inventory';
import { isConditionMet } from './conditions';
import {
  eraCostMultiplier,
  eraModifier,
  eraTierMultiplier,
  expedition,
  fossilMultiplier,
} from './rebirth';
import { NIGHT_SKY_OFFLINE } from '../data/museum';

/** Price growth per level (the standard incremental-game curve). */
export const COST_GROWTH = 1.15;

/** Cost of the next level given a base cost and the current level (0 = unhatched). */
export function nextCost(baseCost: number, level: number): number {
  return Math.ceil(baseCost * COST_GROWTH ** level);
}

/** Total cost of `count` more levels starting from `level`. */
export function bulkCost(baseCost: number, level: number, count: number): number {
  // Geometric series: base * r^level * (r^count - 1) / (r - 1)
  const r = COST_GROWTH;
  return Math.ceil((baseCost * r ** level * (r ** count - 1)) / (r - 1));
}

// ---------------------------------------------------------------------------
// Multipliers

/** Eggs granted by a click before any upgrades. */
export const BASE_CLICK_VALUE = 1;

/**
 * EPS bonus per achievement earned (the plan's "cheap and recommended" open
 * decision). Set to 0 to make achievements purely cosmetic.
 */
export const ACHIEVEMENT_EPS_BONUS = 0.01;

export interface Multipliers {
  /** Per-tier production multiplier; tiers absent from the map are ×1. */
  dino: Readonly<Record<string, number>>;
  /** Applied to all production. */
  global: number;
  /** Flat eggs added to each click. */
  clickFlat: number;
  /** Fraction of EPS added to each click. */
  clickEpsPct: number;
}

const NO_MULTIPLIERS: Multipliers = { dino: {}, global: 1, clickFlat: 0, clickEpsPct: 0 };

/**
 * The upgrades that count right now. A Barren era means no upgrades at all:
 * the ones Warm Blood and Long Memory carry in stay owned (so they carry on
 * into the era after) but do nothing until then.
 */
function activeUpgrades(state: GameState): readonly string[] {
  return upgradesAllowed(state) ? state.upgrades : [];
}

/** Folds every bought upgrade into one set of multipliers. */
export function multipliersFromState(state: GameState): Multipliers {
  const upgrades = activeUpgrades(state);
  if (upgrades.length === 0 && state.achievements.length === 0 && state.fossils === 0) {
    return NO_MULTIPLIERS;
  }
  const dino: Record<string, number> = {};
  let global = 1;
  let clickFlat = 0;
  let clickEpsPct = 0;

  for (const id of upgrades) {
    const def = UPGRADES_BY_ID.get(id);
    if (!def) continue; // An id from a newer save; ignore rather than crash.
    const e = def.effect;
    switch (e.kind) {
      case 'dinoMult':
        dino[e.dinoId] = (dino[e.dinoId] ?? 1) * e.mult;
        break;
      case 'globalMult':
        global *= e.mult;
        break;
      case 'clickFlat':
        clickFlat += e.amount;
        break;
      case 'clickEpsPct':
        clickEpsPct += e.pct;
        break;
      case 'offlineRate':
        break; // read by offlineRate(), not a production multiplier
    }
  }

  global *= 1 + state.achievements.length * ACHIEVEMENT_EPS_BONUS;
  global *= fossilMultiplier(state);
  return { dino, global, clickFlat, clickEpsPct };
}

// ---------------------------------------------------------------------------
// Production

/** Eggs per second from one dinosaur at a level, before multipliers. */
export function epsForDino(baseEps: number, level: number): number {
  return baseEps * level;
}

/**
 * Eggs per second given the whole state. Pure and clock-free, so the live
 * tick, the offline-progress calculation, and the tests all share it.
 *
 * `boost` is the slot M5's falling-egg bonuses feed into; offline progress
 * deliberately passes 1 (unboosted).
 */
export function epsFromState(state: GameState, boost = 1): number {
  let eps = 0;
  for (const value of Object.values(epsBreakdown(state, boost))) eps += value;
  return eps;
}

/**
 * Each hatched dinosaur's actual contribution to EPS, with every multiplier
 * applied (its doublers, global upgrades, achievements, boost), so the shop
 * can show real numbers and they sum to the EPS readout. Unhatched tiers are
 * omitted.
 */
export function epsBreakdown(state: GameState, boost = 1): Record<string, number> {
  const m = multipliersFromState(state);
  const out: Record<string, number> = {};
  for (const dino of DINOS) {
    const level = dinoLevel(dino, state);
    if (level > 0) out[dino.id] = dinoEpsWith(m, dino, level, state, boost);
  }
  return out;
}

/**
 * What one dinosaur lays at a given level with every multiplier in force
 * now. For a tier not yet hatched, level 1 is what hatching it would add:
 * the shop's preview, which used to show the bare base rate and so read far
 * lower than the herd's real numbers once achievements and fossils counted.
 */
export function dinoEpsAt(dino: DinoDef, level: number, state: GameState, boost = 1): number {
  return dinoEpsWith(multipliersFromState(state), dino, level, state, boost);
}

function dinoEpsWith(
  m: ReturnType<typeof multipliersFromState>,
  dino: DinoDef,
  level: number,
  state: GameState,
  boost: number,
): number {
  return (
    epsForDino(dino.baseEps, level) *
    evolutionMultiplier(dino, state) *
    eraTierMultiplier(state, dino) *
    (m.dino[dino.id] ?? 1) *
    m.global *
    boost
  );
}

/** Eggs a click is worth: base plus flat bonuses plus a share of EPS. */
export function clickValue(state: GameState, eps = 0): number {
  const m = multipliersFromState(state);
  const era = eraModifier(state);
  if (expedition(state)?.noClicks === true) return 0;
  return (BASE_CLICK_VALUE + m.clickFlat + eps * m.clickEpsPct) * (era?.clicks ?? 1);
}

/** Eggs produced over a span. Used by the tick and by offline progress in M4. */
export function production(eps: number, seconds: number): number {
  return eps * Math.max(0, seconds);
}

/**
 * Fraction of normal production earned while the game is closed. Resting
 * dinosaurs lay at a tenth of their rate; the offline upgrades and the shard
 * shop's nights add to it.
 */
export const OFFLINE_RATE_BASE = 0.1;

export function offlineRate(state: GameState): number {
  let rate = OFFLINE_RATE_BASE;
  for (const id of activeUpgrades(state)) {
    const e = UPGRADES_BY_ID.get(id)?.effect;
    if (e?.kind === 'offlineRate') rate += e.add;
  }
  for (const id of state.shop) {
    const e = SHOP_ITEMS_BY_ID.get(id)?.effect;
    if (e?.kind === 'offlineRate') rate += e.add;
  }
  // The Museum's own night. The shard shop's nights are the era's and go
  // with it; this one is permanent, which is what makes it worth fossils.
  if (state.museum.includes('museum:night-sky')) rate += NIGHT_SKY_OFFLINE;
  // The era's own weather, applied last so it scales everything else.
  return rate * (eraModifier(state)?.offline ?? 1);
}

// ---------------------------------------------------------------------------
// The shard shop

export function hasShopItem(def: ShopItemDef, state: GameState): boolean {
  return state.shop.includes(def.id);
}

/** How many times a repeatable tile has been bought. */
export function timesBought(def: ShopItemDef, state: GameState): number {
  return state.shop.filter((id) => id === def.id).length;
}

/** What the next one costs: flat for a one-off, growing for a repeatable tile. */
export function shopItemPrice(def: ShopItemDef, state: GameState): number {
  if (!def.repeatable) return def.price;
  return Math.ceil(def.price * REPEAT_GROWTH ** timesBought(def, state));
}

/** On sale: its prerequisite (if any) is owned, and it is repeatable or unbought. */
export function isShopItemAvailable(def: ShopItemDef, state: GameState): boolean {
  if (!def.repeatable && hasShopItem(def, state)) return false;
  return def.requires === undefined || state.shop.includes(def.requires);
}

/** Buys a shop item with shards if it is available and affordable. Returns whether it bought. */
export function buyShopItem(def: ShopItemDef, state: GameState): boolean {
  if (!isShopItemAvailable(def, state)) return false;
  const price = shopItemPrice(def, state);
  if (state.shards < price) return false;
  state.shards -= price;
  state.shop.push(def.id);
  if (def.effect.kind === 'grantItem') addItem(state, def.effect.itemId);
  return true;
}

// ---------------------------------------------------------------------------
// Dinosaurs. Each tier is one dinosaur the player hatches (level 1) and then
// levels up; it lays more and grows bigger in the yard as its level rises.

/** Current level of a tier; 0 until hatched. */
export function dinoLevel(def: DinoDef, state: GameState): number {
  return state.dinos[def.id] ?? 0;
}

/** A tier appears in the shop once lifetime eggs reach this fraction of its base cost. */
export const REVEAL_FRACTION = 0.1;

/** Price of hatching (level 0 → 1) or of the next level up. */
export function dinoCost(def: DinoDef, state: GameState): number {
  return Math.ceil(nextCost(def.baseCost, dinoLevel(def, state)) * eraCostMultiplier(state, def));
}

export function canAffordDino(def: DinoDef, state: GameState): boolean {
  return state.eggs >= dinoCost(def, state);
}

/**
 * Whether a tier shows in the shop. Reveal keys off lifetime eggs rather than
 * the current balance, so a tier never vanishes again after the player spends
 * down, and anything already hatched always stays visible.
 */
export function isDinoRevealed(def: DinoDef, state: GameState): boolean {
  // A resident is only ever in its own era: nowhere else to find it.
  if (!isDinoAvailable(def, state)) return false;
  if (dinoLevel(def, state) > 0) return true;
  return state.totalEggs >= def.baseCost * REVEAL_FRACTION;
}

/** Whether this era offers the dinosaur: every one but another era's resident. */
export function isDinoAvailable(def: DinoDef, state: GameState): boolean {
  return def.era === undefined || def.era === state.eraId;
}

/** Hatches or levels up a dinosaur if affordable, deducting the price. Returns whether it did. */
export function buyDino(def: DinoDef, state: GameState): boolean {
  if (!isDinoAvailable(def, state)) return false;
  const cost = dinoCost(def, state);
  if (state.eggs < cost) return false;
  state.eggs -= cost;
  state.dinos[def.id] = (state.dinos[def.id] ?? 0) + 1;
  return true;
}

// ---------------------------------------------------------------------------
// Upgrades

export function hasUpgrade(def: UpgradeDef, state: GameState): boolean {
  return state.upgrades.includes(def.id);
}

/** Unlocked and not yet bought: the set the shop shows. */
/** Barren buys nothing: the upgrades are simply not there for the era. */
export function upgradesAllowed(state: GameState): boolean {
  return expedition(state)?.noUpgrades !== true;
}

export function isUpgradeAvailable(def: UpgradeDef, state: GameState, eps: number): boolean {
  if (!upgradesAllowed(state)) return false;
  return !hasUpgrade(def, state) && isConditionMet(def.unlock, state, eps);
}

/** Buys an upgrade if it is available and affordable. Returns whether it bought. */
export function buyUpgrade(def: UpgradeDef, state: GameState, eps: number): boolean {
  if (!isUpgradeAvailable(def, state, eps)) return false;
  if (state.eggs < def.cost) return false;
  state.eggs -= def.cost;
  state.upgrades.push(def.id);
  return true;
}

// ---------------------------------------------------------------------------
// Achievements

/**
 * Awards every achievement whose condition is now met and returns the newly
 * earned ones, so the caller can toast them. Cheap enough to run every tick.
 */
export function checkAchievements(state: GameState, eps: number): AchievementDef[] {
  const earned: AchievementDef[] = [];
  for (const def of ACHIEVEMENTS) {
    if (state.achievements.includes(def.id)) continue;
    if (!isConditionMet(def.condition, state, eps)) continue;
    state.achievements.push(def.id);
    earned.push(def);
  }
  return earned;
}
